import { useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import type { LinkMenuItem, MenuItem, NavigationConfig, NavigationModuleBehavior } from '@core/ui/navigation';
import { getTenantById, isTenantExpCA, isTenantExpUS } from '@tenants';
import { getTenantOrganizationConfig } from '@tenants/utils/configs';
import type { TenantId } from 'tenants/types';
import { zoocasaNavigationConfig } from 'tenants/zoocasa/config/navigation';
import { useTranslatedHeaderConfig } from 'hooks/use-translated-header-config';
import { useLocale } from 'hooks/use-locale';
import { ACCESSIBILITY_PATH } from 'constants/urls';
import { Namespaces } from 'types/locales';

import type { InternationalLinksOptions } from 'components/modal-dialog/international-links';

// Language submenu in hamburger menu on rail bar
export type RailLocaleMenu = {
  label: string;
  currentLocale: string;
  options: { id: string; label: string }[];
  onSelect: (localeId: string) => void;
};

// Config that drives the Area Page V2 left-rail logo + flyout menu links.
export type RailMenuConfig = {
  logoSrc: string;
  logoAlt: string;
  navConfig: NavigationConfig;
  localeMenu?: RailLocaleMenu;
};

const ZOOCASA_MENU_CONFIG: RailMenuConfig = {
  logoSrc: '/assets/images/zoocasa-vector.svg',
  logoAlt: 'Zoocasa',
  navConfig: zoocasaNavigationConfig,
};

const EXP_LOGO_SRC = '/assets/images/exp-logo-black.svg';
const EXP_LOGO_ALT = 'eXp Realty';

// The rail flyout only reads navConfig.structure, but NavigationConfig still requires a
// behavior block. eXp rails have no dropdowns, so these defaults are never exercised.
const RAIL_NAV_BEHAVIOR: NavigationModuleBehavior = {
  enableSubmenus: true,
  mobileExpandInline: true,
  closeOnOutsideClick: true,
  dropdownTrigger: 'hover',
};

// Shape of a single translated header menu item (from useTranslatedHeaderConfig).
type TranslatedHeaderMenuItem = {
  id: string;
  label: string;
  link?: string;
  newTab?: boolean;
  mobileOnly?: boolean;
};

// Opens the same international-links modal the eXp site header's globe button uses.
export const GLOBAL_PRESENCE_ITEM_ID = 'global-presence';
export const OPEN_INTERNATIONAL_LINKS_HANDLER = 'openInternationalLinks';

// Builds a rail NavigationConfig from a tenant's (already translated) header menu items.
export function buildNavConfigFromHeaderMenuItems(
  menuItems: TranslatedHeaderMenuItem[],
  globalPresence?: { label: string; links: InternationalLinksOptions }
): NavigationConfig {
  const menu: MenuItem[] = menuItems
    .filter((item): item is TranslatedHeaderMenuItem & { link: string } => Boolean(item.link))
    .map((item): LinkMenuItem => {
      // The accessibility entry must open the EqualWeb widget (like the site header does),
      // not navigate to /accessibility. RailMenu runs the 'openAccessibilityMenu' handler.
      if (item.link === ACCESSIBILITY_PATH) {
        return {
          id: item.id,
          type: 'link',
          label: item.label,
          href: '#',
          metadata: { handler: 'openAccessibilityMenu' },
        };
      }
      return {
        id: item.id,
        type: 'link',
        label: item.label,
        href: item.link,
        external: item.newTab,
      };
    });

  if (globalPresence) {
    menu.push({
      id: GLOBAL_PRESENCE_ITEM_ID,
      type: 'link',
      label: globalPresence.label,
      href: '#',
      metadata: {
        handler: OPEN_INTERNATIONAL_LINKS_HANDLER,
        params: { links: globalPresence.links },
      },
    });
  }

  return {
    module: { behavior: RAIL_NAV_BEHAVIOR },
    structure: { header: [], mobile: [], menu },
  };
}

// Resolves the logo + navigation config for the Area Page V2 left rail.
export function useRailMenuConfig(tenantId: TenantId): RailMenuConfig {
  const isExp = isTenantExpCA(tenantId) || isTenantExpUS(tenantId);
  const { menuItems } = useTranslatedHeaderConfig(tenantId);
  const { t } = useTranslation(Namespaces.COMMON);
  const tenant = getTenantById(tenantId);
  const internationalLinks = getTenantOrganizationConfig(tenant)?.internationalLinks;
  const { isEnabled, isReady, currentLocale, localeOptions, changeLocale } = useLocale(tenantId);
  // The rail offers a language switcher only for tenants that opted into global locale switching.
  const canChangeLanguage = isEnabled && isReady && tenant.localization.allowGlobalLocaleSwitching === true;

  const localeMenu = useMemo<RailLocaleMenu | undefined>(() => {
    if (!canChangeLanguage) {
      return undefined;
    }
    return {
      label: t('language.menu_label'),
      currentLocale,
      options: localeOptions.map(option => ({ id: option.id, label: option.label })),
      onSelect: (localeId: string) => {
        const option = localeOptions.find(candidate => candidate.id === localeId);
        option?.onClick?.();
        void changeLocale(localeId);
      },
    };
  }, [canChangeLanguage, currentLocale, localeOptions, changeLocale, t]);

  return useMemo(() => {
    if (!isExp) {
      return { ...ZOOCASA_MENU_CONFIG, localeMenu };
    }
    const globalPresence = internationalLinks
      ? { label: t('international.menu_label'), links: internationalLinks }
      : undefined;

    return {
      logoSrc: EXP_LOGO_SRC,
      logoAlt: EXP_LOGO_ALT,
      navConfig: buildNavConfigFromHeaderMenuItems(menuItems, globalPresence),
      localeMenu,
    };
  }, [isExp, menuItems, internationalLinks, localeMenu, t]);
}
